import { Brick, registerBrick, Query, ErrorFlow } from 'olympe';

const cleanseQuery = (query) => {
    const pattern = /':p(\d+)'/g;
    return query.replace(pattern, ":p$1");
}

const seperateValueBasedonTableName = (cleanedData) => Object.entries(cleanedData).reduce((acc, curr) => {

    const key = curr[0];

    if (key !== "#tag" && key !== "#type") {

        curr[1].tableName.forEach((tableName, index) => {

            if (!acc[tableName]){
                acc[tableName] = {};
                acc[tableName].queryOperation = curr[1].queryOperations[index];
                acc[tableName].entries = [];
            }

            acc[tableName].entries.push({
                key,
                value: curr[1].value[index]
            })
        }
        )
    }

    return acc;
}, {})

const removeIfNotInMapper = ({ mapper, data, bindings, finalConditions = {}, isUpdate = false }) => {

    const finalData = {};

    const createBindings = ({ value, table }) => {

        if (!bindings[table])
            bindings[table] = {
                paramIndex: 0
            }

        // --- MODIFICATION HERE: Removed the check for 'null' to allow it to be bound as a parameter ---
        
        const newParam = `p${bindings[table].paramIndex}`;
        const binding = `:${newParam}`;

        bindings[table] = {
            ...bindings[table],
            [newParam]: value,
            paramIndex: bindings[table].paramIndex + 1
        }

        return binding;
    };

    const fieldToColumn = (key) => mapper[key];

    Object.keys(mapper).forEach((key) => {

        const column = fieldToColumn(key)?.columnName;

        mapper[key].tableNames.forEach((table, index) => {

            // --- MODIFICATION HERE: Pass 'null' to createBindings if data[key] is undefined and it's an INSERT (not isUpdate) ---
            const valueForBinding = data[key] === undefined && !isUpdate ? null : data[key];

            // Only skip if column is missing OR if we're in update mode and the value is strictly UNDEFINED
            if (column && !(isUpdate && valueForBinding === undefined && !finalConditions[column])) {

                // If we're updating and finalConditions has the column, we must include it, regardless of valueForBinding being undefined
                if (column && isUpdate && valueForBinding === undefined && !finalConditions[column]) {
                    return;
                }

                if (!finalData[column]) {
                    finalData[column] = {}
                    finalData[column].tableName = []
                    finalData[column].queryOperations = []
                    finalData[column].value = []
                }

                // Value passed to createBindings will be either the actual data, or null if it was undefined in an INSERT.
                const newBinding = createBindings({ value: valueForBinding, table }); 
                const queryOperation = mapper[key].queryOperations[index];

                finalData[column].tableName.push(table)
                finalData[column].queryOperations.push(queryOperation)
                finalData[column].value.push(newBinding)

            }
        });

        // Remove this line: we no longer delete nulls
        // delete data[key];
    });

    Object.keys(bindings).forEach(tableName => {
        delete bindings[tableName].paramIndex;
    });

    return finalData;
};

const generateUpdateQuery = ({ mapper, updateConditions, data }) => {

    let bindings = []

    const finalConditions = updateConditions.reduce((acc, curr) => {
        const dbKey = Object.entries(curr)[0][0];
        const dataKey = Object.entries(curr)[0][1];
        acc[dbKey] = data[dataKey];
        return acc;
    }, {})

    const cleanedData = removeIfNotInMapper({ mapper, data, bindings, finalConditions, isUpdate: true });

    const seperatedValueBasedonTableName = seperateValueBasedonTableName(cleanedData);

    const queries = Object.keys(seperatedValueBasedonTableName).map(tableName => {

        const updates = seperatedValueBasedonTableName[tableName].entries
            .map(row => {
                const { key, value } = row;
                // 'value' here is the binding placeholder (e.g., ':p0'). It will only be "NULL" if
                // explicitly forced earlier, but with the new logic, it should be a binding parameter.
                if (value === "NULL") {
                    return `${key} = NULL`;
                } else {
                    return `${key} = ${value}`; // Use binding placeholder directly
                }
            }).join(", ");

        // Create the condition string from the conditions object
        const conditionStrings = Object.keys(finalConditions)
            .map((key) => {
                const value = finalConditions[key];
                if (typeof value === "string") {
                    // Escape single quotes in the condition value
                    return `${key} = '${value.replace(/'/g, "''")}'`;
                } else if (value === null) {
                    return `${key} IS NULL`;
                } else {
                    return `${key} = ${value}`;
                }
            })
            .join(" AND ");

        return cleanseQuery(`UPDATE ${tableName} SET ${updates} WHERE ${conditionStrings};`);
    })

    // Construct the UPDATE query
    return {
        queries,
        bindings
    }
};

const generateInsertQuery = ({ mapper, isUpsert, data, conflictKeys }) => {
    let bindings = [];

    const cleanedData = removeIfNotInMapper({ mapper, data, bindings });
    const separatedByTable = seperateValueBasedonTableName(cleanedData);

    const queries = Object.keys(separatedByTable).map(tableName => {
        const {entries, queryOperation } = separatedByTable[tableName];
        const columns = entries.map(({ key }) => key);
        
        // 'value' is now guaranteed to be a binding placeholder (e.g., ':p0') for all values, including null
        const values = entries.map(({ value }) => value); 

        if (!isUpsert || queryOperation === "insert"){
            // Simple INSERT
            return `INSERT INTO ${tableName} (${columns.join(', ')}) VALUES (${values.join(', ')});`;
        }

        // UPSERT via MERGE
        const conflictCols = conflictKeys[tableName];
        if (!conflictCols) {
            throw new Error(`Conflict keys not defined for table: ${tableName}`);
        }

        const sourceAlias = 'source';
        const targetAlias = 'target';

        const onClause = conflictCols
            .map(col => `${targetAlias}.${col} = ${sourceAlias}.${col}`)
            .join(' AND ');

        const updateAssignments = columns
            .filter(col => !conflictCols.includes(col))
            .map(col => `${col} = ${sourceAlias}.${col}`)
            .join(', ');

        const mergeQuery = `MERGE INTO ${tableName} AS ${targetAlias} USING (VALUES (${values.join(', ')})) AS ${sourceAlias} (${columns.join(', ')}) ON ${onClause} WHEN MATCHED THEN UPDATE SET ${updateAssignments} WHEN NOT MATCHED THEN INSERT (${columns.join(', ')}) VALUES (${columns.map(col => `${sourceAlias}.${col}`).join(', ')});`;

        return mergeQuery.trim();
    });

    return { queries, bindings };
};


const generateDeleteQuery = ({ mapper, updateConditions, data }) => {

    let bindings = []

    const finalConditions = updateConditions.reduce((acc, curr) => {
        const dbKey = Object.entries(curr)[0][0];
        const dataKey = Object.entries(curr)[0][1];
        acc[dbKey] = data[dataKey];
        return acc;
    }, {})

    const cleanedData = removeIfNotInMapper({ mapper, data, bindings, finalConditions, isUpdate: true });

    const seperatedValueBasedonTableName = seperateValueBasedonTableName(cleanedData);

    const queries = Object.keys(seperatedValueBasedonTableName).map(tableName => {

        const updates = seperatedValueBasedonTableName[tableName].entries
            .map(row => {
                const { key, value } = row;
                // 'value' here is the binding placeholder (e.g., ':p0')
                if (value === "NULL") {
                    return `${key} = NULL`;
                } else {
                    return `${key} = ${value}`; // Use binding placeholder directly
                }
            }).join(", ");

        // Create the condition string from the conditions object
        const conditionStrings = Object.keys(finalConditions)
            .map((key) => {
                const value = finalConditions[key];
                if (typeof value === "string") {
                    // Escape single quotes in the condition value
                    return `${key} = '${value.replace(/'/g, "''")}'`;
                } else if (value === null) {
                    return `${key} IS NULL`;
                } else {
                    return `${key} = ${value}`;
                }
            })
            .join(" AND ");

        return cleanseQuery(`UPDATE ${tableName} SET ${updates} WHERE ${conditionStrings};`);
    })

    // Construct the UPDATE query
    return {
        queries,
        bindings
    }
};

const generateQuery = ({ modelTag, updateConditions, payload, isUpsert }) => {

    const { toDelete, toInsert, toUpdate } = payload;

    const paramConfigs = Query.instancesOf(modelTag, false, "server").executeFromCache(); 

    const mapper = paramConfigs.reduce((acc, curr) => {
        const column = curr.get("0185c081050d996c424c");
        const field = curr.get("0185c081098c285960d8");
        const queryOperation = curr.get("01879d93b621833b5c5f");
        const isPrimaryKey = !!curr.get("0187b278b39c26a45233");

        const tableName = `${curr.get('01862c935347456d04b2')}.${curr.get('0185c080fff9d4533458')}`

        if (!acc[field] && !Array.isArray(acc[field]?.tableNames)) {
            acc[field] = {}
            acc[field].tableNames = [];
            acc[field].isPrimaryKey = [];
            acc[field].queryOperations = [];
        }

        acc[field] = {
            tableNames: [...acc[field].tableNames, tableName],
            columnName: `${column}`,
            isPrimaryKey: [...acc[field].isPrimaryKey, isPrimaryKey],
            queryOperations: [...acc[field].queryOperations, queryOperation]

        }
        return acc;
    }, {});


    const generateConflictKeys = (mapper) => {
        const conflictKeys = {};

        for (const [field, props] of Object.entries(mapper)) {

            props.tableNames.forEach((table, index) => {
                if (!conflictKeys[table]) {
                    conflictKeys[table] = [];
                }

                if (props.isPrimaryKey[index]) {
                    conflictKeys[table].push(props.columnName);
                }

            });

        }

        return conflictKeys;
    }


    const conflictKeys = generateConflictKeys(mapper);

    const toUpdateMerged = [...(Array.isArray(toUpdate) ? toUpdate : [])];
    const insertItemsCount = toInsert?.length || 0;
    const updateItemsCount = toUpdateMerged?.length || 0;
    const deleteItemsCount = toDelete?.length || 0;

    const insertQueries = insertItemsCount > 0 ? toInsert
        .map((item) => {
            const { queries, bindings: bindingsObject } = generateInsertQuery({ mapper, isUpsert, data: item, conflictKeys });
            const bindings = Object.values(bindingsObject);

            return queries.map((query, index) => ({
                query,
                bindings: bindings[index]
            }))

        }) : []


    const updateQueries = updateItemsCount > 0 ? toUpdateMerged
        .map((item) => {
            const { queries, bindings: bindingsObject } = generateUpdateQuery({ mapper, updateConditions, data: item })
            const bindings = Object.values(bindingsObject);

            return queries.map((query, index) => ({
                query,
                bindings: bindings[index]
            }))

        }) : [];

    const deleteQueries = deleteItemsCount > 0 ? toDelete
        .map((item) => {
            const { queries, bindings: bindingsObject } = generateDeleteQuery({ mapper, updateConditions, data: item })
            const bindings = Object.values(bindingsObject);

            return queries.map((query, index) => ({
                query,
                bindings: bindings[index]
            }))

        }) : [];

    return [...insertQueries.flat(), ...updateQueries.flat(), ...deleteQueries.flat()]
}


export default class BuildTransactionSQLQueryV5 extends Brick {

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {ListDef} modelTags
     * @param {*} payload
     * @param {function()} forwardEvent
     * @param {function(string)} setQuery
     * @param {function(*)} setBindings
     * @param {function(*)} setErrorFlow
     */
    update($, [modelTags, payload, updateConditions, isUpsert], [forwardEvent, setQuery, setErrorFlow]) {

        try {

            const queries = payload.reduce((acc, curr, index) => {
                const newQuery = generateQuery({ modelTag: modelTags[index], updateConditions: updateConditions ? updateConditions[index] || [] : [], payload: curr, isUpsert });
                return [...acc, ...newQuery]
            }, []
            )

            setQuery(queries);
            forwardEvent();

        } catch (error) {
            console.log(error)
            setErrorFlow(ErrorFlow.create(`Error ${error} during the execution`));

        }
    }
}

registerBrick('0196caa216cd9eecf2bc', BuildTransactionSQLQueryV5);